import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { FileText } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useCreateInvoice, type InvoiceCreatePayload } from '@/api/invoices';
import { useSettings } from '@/api/settings';
import type { Claim } from '@/api/claims';

/*
 * Creates an invoice from a pre-filtered set of client-billed, Ready claims
 * belonging to a single client. Caller is responsible for enforcing the
 * "single client" rule — this dialog trusts its props and posts the bundle
 * as-is.
 *
 * Due-date behavior mirrors the server: empty field falls through to
 * `issued_at + settings.billing.invoiceDueDays`. We pre-fill the input with
 * that default so the biller sees what's coming.
 */

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: string;
    clientName: string;
    claims: Claim[];
    subtotal: number;
    onSuccess?: (invoiceId: string) => void;
}

function todayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function addDaysIso(iso: string, days: number): string {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

export function CreateInvoiceDialog({
    open, onOpenChange, clientId, clientName, claims, subtotal, onSuccess,
}: Props) {
    const navigate = useNavigate();
    const mutation = useCreateInvoice();
    const { data: settings } = useSettings();

    const defaultDueDays = useMemo(() => {
        const v = Number(settings?.billing?.invoiceDueDays);
        return Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
    }, [settings]);

    const [dueDate, setDueDate] = useState(addDaysIso(todayIso(), defaultDueDays));
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            setDueDate(addDaysIso(todayIso(), defaultDueDays));
            setNotes('');
            mutation.reset();
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const count = claims.length;
    const dueInvalid = !/^\d{4}-\d{2}-\d{2}$/.test(dueDate);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (count === 0 || dueInvalid) return;
        const payload: InvoiceCreatePayload = {
            clientId,
            claimIds: claims.map((c) => c.id),
            dueDate,
            notes: notes.trim() || undefined,
        };
        try {
            const inv = await mutation.mutateAsync(payload);
            onSuccess?.(inv.id);
            onOpenChange(false);
            navigate(`/billing/invoices/${inv.id}`);
        } catch {
            /* surfaced below via mutation.error */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            const data = e.response?.data as { error?: string; code?: string; detail?: unknown } | undefined;
            return data?.error ?? 'Failed to create invoice';
        }
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),40rem)]">
                <DialogHeader>
                    <DialogTitle>
                        Create invoice for {clientName}
                    </DialogTitle>
                    <DialogDescription>
                        Bundle {count} ready claim{count === 1 ? '' : 's'} onto a single invoice.
                        The bundled claims flip to Submitted and their billing fields lock until the invoice is voided.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField
                            label="Due date"
                            required
                            hint={`Defaults to today + ${defaultDueDays} days from billing settings.`}
                            error={dueInvalid ? 'Required' : undefined}
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min={new Date().toISOString().slice(0, 10)}
                                    max="9999-12-31"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                />
                            )}
                        </FormField>
                        <div className="text-xs text-text-muted self-end pb-2 text-right tabular-nums">
                            Subtotal: <span className="font-medium text-text">{subtotal.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</span>
                        </div>
                    </div>

                    <FormField label="Notes" hint="Optional — appears on the invoice.">
                        {(id) => (
                            <Textarea
                                id={id}
                                rows={3}
                                maxLength={4000}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="e.g., Payable by credit card at the front desk."
                            />
                        )}
                    </FormField>

                    {count > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto border border-border rounded-md bg-surface-muted p-2 text-xs">
                            {claims.slice(0, 10).map((c) => (
                                <div key={c.id} className="flex items-center justify-between py-0.5">
                                    <span className="font-mono text-text">{c.claimNo}</span>
                                    <span className="truncate px-2 text-text-muted">
                                        {c.serviceDate?.slice(0, 10) || ''} · {c.serviceCode}
                                    </span>
                                    <span className="tabular-nums">
                                        {c.amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                                    </span>
                                </div>
                            ))}
                            {count > 10 && (
                                <div className="pt-1 text-text-subtle">…and {count - 10} more</div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={mutation.isPending || count === 0 || dueInvalid}
                        >
                            <FileText className="w-3.5 h-3.5" />
                            {mutation.isPending ? 'Creating…' : `Create invoice (${count})`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
